import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchAdSummaryByAd } from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  arraysEqual,
  computeDerivedMetrics,
  getLastThirtyDays,
  inferCreativeLabels,
  safeNumber,
} from '../creative-performance/utils';

const AD_NAME_FILTER = 'buzz';

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const mapRow = (row = {}, clientNameById) => {
  const creativeLabels = inferCreativeLabels(row);
  const clientId = row.client_id ? String(row.client_id) : '';
  const base = {
    uuid: row.uuid || `${row.ad_id || row.ad_name || ''}-${clientId}`,
    clientId,
    clientName: clientNameById.get(clientId) || row.client_name || row.client__name || '',
    parentCompanyId: row.parent_company_id ? String(row.parent_company_id) : '__none__',
    adId: row.ad_id || '',
    adName: row.ad_name || row.ad || '',
    adSetName: row.ad_set_name || '',
    campaign: row.campaign || '',
    isActive: row.active == null ? true : Boolean(row.active),
    headline: row.headline || '',
    primaryText: row.primary_text || '',
    description: row.description || '',
    destinationUrl: row.destination_url || '',
    ctaType: row.cta_type || '',
    thumbnailUrl: row.thumbnail_url || '',
    imageUrl: row.image_url || '',
    facebookFormat: creativeLabels.facebookFormat,
    adType: creativeLabels.adType,
    spend: safeNumber(row.total_spend_sum),
    impressions: safeNumber(row.impressions_sum),
    clicks: safeNumber(row.clicks_sum),
    leads: safeNumber(row.total_leads_sum),
  };
  return { ...base, ...computeDerivedMetrics(base) };
};

function DesignerPreviewDialog({ row, onClose }) {
  if (!row) return null;
  const previewSrc = row.imageUrl || row.thumbnailUrl;
  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{row.adName || 'Ad Preview'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Client</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.clientName || '-'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Status</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.isActive ? 'Active' : 'Paused'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Format</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.facebookFormat || row.adType || '-'}</Typography>
            </Box>
          </Stack>
          {previewSrc ? (
            <Box
              component="img"
              src={previewSrc}
              alt={row.adName || 'Ad creative preview'}
              sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}
            />
          ) : null}
          <Box>
            <Typography variant="overline" color="text.secondary">Headline</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>{row.headline || 'No headline stored.'}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Primary Text</Typography>
            <Typography variant="body2">{row.primaryText || 'No primary text stored.'}</Typography>
          </Box>
          {row.description ? (
            <Box>
              <Typography variant="overline" color="text.secondary">Description</Typography>
              <Typography variant="body2">{row.description}</Typography>
            </Box>
          ) : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Destination</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{row.destinationUrl || '-'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">CTA</Typography>
              <Typography variant="body2">{row.ctaType || '-'}</Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' } }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Spend</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><DollarFormatter value={row.spend} /></Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Impressions</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.impressions.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">CTR</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><PercentageFormatter value={row.ctr} /></Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Leads</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.leads.toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">CPL</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '-'}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function DesignerCreativePerformance() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [previewRow, setPreviewRow] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
        })));
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, []);

  const clientNameById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((c) => next.set(c.id, c.name));
    return next;
  }, [clientMetadata]);

  const eligibleClients = React.useMemo(
    () => clientMetadata.filter((c) => !selectedParentCompanies.length || selectedParentCompanies.includes(c.parentCompanyId)),
    [clientMetadata, selectedParentCompanies]
  );

  const parentCompanyOptions = React.useMemo(() => {
    const deduped = new Map();
    eligibleClients.forEach((c) => {
      if (!deduped.has(c.parentCompanyId)) deduped.set(c.parentCompanyId, { id: c.parentCompanyId, name: c.parentCompanyName });
    });
    return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  const clientOptions = React.useMemo(
    () => eligibleClients.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [eligibleClients]
  );

  React.useEffect(() => {
    const allowed = new Set(parentCompanyOptions.map((o) => o.id));
    setSelectedParentCompanies((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [parentCompanyOptions]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [clientOptions]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? eligibleClients.filter((c) => selectedClients.includes(c.id))
      : eligibleClients;
    return scopedClients.map((c) => c.id);
  }, [eligibleClients, selectedClients]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: '-total_spend_sum',
          ad_name: AD_NAME_FILTER,
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
        };
        const response = await fetchAdSummaryByAd(payload);
        const data = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.results) ? response.data.results : [];
        if (!isMounted) return;
        setRows(
          data
            .map((row) => mapRow(row, clientNameById))
            .sort((a, b) => b.spend - a.spend)
        );
      } catch (err) {
        console.error('Failed to load designer creative performance:', err);
        if (isMounted) { setRows([]); setError('Unable to load creative performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [clientNameById, effectiveClientIds, endDate, loadingClientMeta, selectedParentCompanies, startDate]);

  const totals = React.useMemo(() => {
    const base = rows.reduce((acc, row) => {
      acc.spend += row.spend;
      acc.impressions += row.impressions;
      acc.clicks += row.clicks;
      acc.leads += row.leads;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, leads: 0 });
    return { ...base, ...computeDerivedMetrics(base) };
  }, [rows]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  return (
    <Page title="Designer Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box sx={{ minWidth: 280, maxWidth: { lg: 320 } }}>
                <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
              </Box>
              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 200, flex: 1 }}
                options={parentCompanyOptions.map((o) => ({ label: o.name, value: o.id }))}
                value={parentCompanyOptions.filter((o) => selectedParentCompanies.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                onChange={(_, v) => setSelectedParentCompanies(v.map((o) => o.value))}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                getOptionLabel={(o) => o.label || ''}
                renderInput={(params) => <TextField {...params} label="Parent Companies" />}
              />
              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 200, flex: 1 }}
                options={clientOptions.map((o) => ({ label: o.name, value: o.id }))}
                value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                onChange={(_, v) => setSelectedClients(v.map((o) => o.value))}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                getOptionLabel={(o) => o.label || ''}
                renderInput={(params) => <TextField {...params} label="Clients" />}
              />
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Spend</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Leads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.leads.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Ads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{rows.length}</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading || loadingClientMeta ? (
              <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 88 }}>Preview</TableCell>
                      <TableCell>Ad</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Spend</TableCell>
                      <TableCell align="right">Impressions</TableCell>
                      <TableCell align="right">CTR</TableCell>
                      <TableCell align="right">Leads</TableCell>
                      <TableCell align="right">CPL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No ads matching &quot;{AD_NAME_FILTER}&quot; in this range.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : rows.map((row) => {
                      const thumb = row.thumbnailUrl || row.imageUrl;
                      return (
                        <TableRow
                          key={row.uuid}
                          hover
                          onClick={() => setPreviewRow(row)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            {thumb ? (
                              <Box
                                component="img"
                                src={thumb}
                                alt={row.adName}
                                sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}
                              />
                            ) : (
                              <Box sx={{ width: 72, height: 72, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" color="text.secondary">No image</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.adName || '(no name)'}
                            </Typography>
                            {row.headline ? (
                              <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {row.headline}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{row.clientName || '-'}</TableCell>
                          <TableCell>{row.isActive ? 'Active' : 'Paused'}</TableCell>
                          <TableCell align="right"><DollarFormatter value={row.spend} /></TableCell>
                          <TableCell align="right">{row.impressions.toLocaleString()}</TableCell>
                          <TableCell align="right"><PercentageFormatter value={row.ctr} /></TableCell>
                          <TableCell align="right">{row.leads.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <DesignerPreviewDialog row={previewRow} onClose={() => setPreviewRow(null)} />
      </Stack>
    </Page>
  );
}
